import _ from 'lodash'

import Parameter, { ParameterList } from '@/models/api/payment/parameter/parameter'
import UserSelect, { SelectOption, SelectOptionList } from '@/models/api/payment/parameter/type/user-select'
import { Payment } from '@/models/api/payment/payment'
import { PaymentType } from '@/models/app-config/behavior/types'
import { log } from '@spa/log'

/**
 * ユーザーの決済方法の選択状態を表現する。
 * 選択状態とは、決済方法そのものと[決済のオプション](https://my-color.esa.io/posts/5340)の組み合わせで表現される。
 * 新たに決済方法が選択された際にオブジェクトを生成し、決済のオプションに変更があった際はメソッドを利用して更新する。
 */
export default class SelectedPayment {

  static empty(): SelectedPayment {
    return new SelectedPayment(null, SelectedOptions.empty())
  }

  static createByPayment(payment: Payment): SelectedPayment {
    return new SelectedPayment(payment, SelectedOptions.createFromParameterList(payment.parameters.onlyUserSelect))
  }

  private constructor(
    public readonly payment: Payment | null,
    private readonly selectedOptions: SelectedOptions
  ) {}

  get isEmpty(): boolean {
    return this.payment === null
  }

  get paymentType(): PaymentType | '' {
    if (this.payment === null) {
      return ''
    }

    return this.payment.id
  }

  get parameterList(): ParameterList<Parameter> {
    if (this.payment === null) {
      return ParameterList.empty()
    }

    return this.payment.parameters
  }

  get optionList(): SelectOptionList {
    return this.selectedOptions.list
  }

  toggleOption(option: SelectOption): SelectedPayment {
    return this.update(this.selectedOptions.toggle(option).list)
  }

  update(options: SelectOptionList): SelectedPayment {
    return new SelectedPayment(this.payment, this.selectedOptions.update(options))
  }
}

export class SelectedOptions {

  static empty(): SelectedOptions {
    return new SelectedOptions(SelectOptionList.empty(), ParameterList.empty())
  }

  static createFromParameterList(parameterList: ParameterList<Parameter>): SelectedOptions {
    return new SelectedOptions(SelectOptionList.empty(), parameterList)
  }

  private constructor(
    public readonly list: SelectOptionList,
    private readonly parameters: ParameterList<Parameter>
  ) {}

  toggle(option: SelectOption): SelectedOptions {
    const targetParameter = this.parameters.find(p => p.id === option.parameterId) as UserSelect | null
    if (!targetParameter) {
      log.warn({
        message: `Parameter ${option.parameterId} is not found.`,
        option,
      })

      return this
    }

    return targetParameter.isSingle ? this.toggleSingle(option) : this.toggleMulti(option)
  }

  update(options: SelectOptionList): SelectedOptions {
    return new SelectedOptions(options, this.parameters)
  }

  private toggleSingle(option: SelectOption): SelectedOptions {
    const optionsExcludeTargetParameterId = this.list.filter(o => o.parameterId !== option.parameterId)

    return this.alreadySelected(option) ?
      this.update(optionsExcludeTargetParameterId) :
      this.update(optionsExcludeTargetParameterId.add(option))
  }

  private toggleMulti(option: SelectOption): SelectedOptions {
    return this.alreadySelected(option) ?
      this.update(this.list.remove(option)) :
      this.update(this.list.add(option))
  }

  private alreadySelected(option: SelectOption): boolean {
    return this.list.find(o => o.parameterId === option.parameterId && o.id === option.id) !== null
  }
}
